import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
} from "@mui/material";
import PaidIcon from "@mui/icons-material/Paid";
import EditIcon from "@mui/icons-material/Edit";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";
import PaginationControls from "../Pagination/PaginationControls";

const PAGE_SIZE = 50;

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

const defaultEdit = { date: "", amount: "", campaign_name: "", description: "", channel: "" };

const MarketingSpendTable = ({ refreshKey = 0, appliedFrom = "", appliedTo = "" }) => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(defaultEdit);
  const [editLoading, setEditLoading] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  // Reset to page 1 when filters or refreshKey change
  useEffect(() => {
    setPage(1);
  }, [appliedFrom, appliedTo, refreshKey]);

  useEffect(() => {
    const fetchSpendData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (appliedFrom) params.append("from_date", appliedFrom);
        if (appliedTo) params.append("to_date", appliedTo);
        params.append("page", page);
        const res = await axiosConfig({ method: "get", url: `/investors/marketing-expense-data/?${params}` });
        const raw = res?.data;
        setTotalCount(raw?.count || 0);
        setRows(raw?.results || (Array.isArray(raw) ? raw : []));
      } catch (e) {
        const errMsg =
          e.response?.data?.detail ||
          e.response?.data?.message ||
          e.message ||
          "Failed to fetch spend data";
        toast.error(errMsg);
      } finally {
        setLoading(false);
      }
    };
    fetchSpendData();
  }, [appliedFrom, appliedTo, page, refreshKey, refetchKey]);

  useEffect(() => {
    axiosConfig({ method: "get", url: "/advertisement/marketing-channel/" })
      .then((res) => {
        const raw = res?.data;
        setChannels(raw?.results || (Array.isArray(raw) ? raw : []));
      })
      .catch(() => {});
  }, []);

  const handleEditOpen = (row) => {
    setEditRow(row);
    setEditForm({
      date: row.date || "",
      amount: row.amount || "",
      campaign_name: row.campaign_name || "",
      description: row.description || "",
      channel: row.channel || "",
    });
  };

  const handleEditSave = async () => {
    setEditLoading(true);
    try {
      const res = await axiosConfig({
        method: "put",
        url: `/advertisement/marketing-expense/${editRow.id}/`,
        data: {
          date: editForm.date,
          amount: editForm.amount,
          campaign_name: editForm.campaign_name,
          description: editForm.description || null,
          channel: editForm.channel,
        },
      });
      toast.success("Spend updated!");
      setEditRow(null);
      setRefetchKey((k) => k + 1);
    } catch (e) {
      const errMsg =
        e.response?.data?.detail ||
        e.response?.data?.message ||
        e.message ||
        "Failed to update spend";
      toast.error(errMsg);
    } finally {
      setEditLoading(false);
    }
  };


  const serialOffset = (page - 1) * PAGE_SIZE;
  const totalSpend = rows.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0);
  const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13 },
    "& .MuiInputLabel-shrink": { backgroundColor: "#fff", px: 0.2, borderRadius: "2px" },
  };
  const isEditValid = editForm.date && editForm.amount && editForm.channel;
  const btnSx = {
    borderRadius: "8px",
    background: "#016551",
    textTransform: "none",
    fontWeight: 600,
    px: 2.5,
    boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
    "&:hover": { background: "linear-gradient(135deg,#1db954,#17a349)" },
  };

  return (
    <>
      <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid #e2e8f0", background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        {/* Header */}
        <Box sx={{ px: 2.5, py: 1.8, background: "#04343a", borderRadius: "14px 14px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PaidIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 16 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: 0.5 }}>MARKETING SPEND</Typography>
          </Box>
          {!loading && (
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
              {totalCount.toLocaleString("en-IN")} record{totalCount !== 1 ? "s" : ""}
            </Typography>
          )}
        </Box>

        {/* Table */}
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["S.No", "Date", "Campaign Name", "Channel", "Amount", "Edit"].map((h) => (
                  <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 6 }}>
                    <CircularProgress size={28} sx={{ color: "#04343a" }} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 5, fontSize: 13, color: "#94a3b8" }}>
                    No spend records found
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {rows.map((row, i) => (
                    <TableRow key={row.id ?? i} sx={{ "&:hover": { background: "#f8fafc" } }}>
                      <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{serialOffset + i + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>{formatDate(row.date)}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{row.campaign_name || "—"}</Typography>
                        {row.description && (
                          <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>{row.description}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.channel_name || "—"}
                          size="small"
                          sx={{ fontSize: 10, fontWeight: 700, height: 20, borderRadius: "6px", background: "#f1f5f9", color: "#64748b", "& .MuiChip-label": { px: 1 } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap" }}>
                        {formatCurrency(row.amount)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton size="small" onClick={() => handleEditOpen(row)} sx={{ color: "#016551", "&:hover": { background: "#f0fdf4" } }}>
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Total Row */}
                  <TableRow sx={{ background: "#f8fafc" }}>
                    <TableCell colSpan={4} sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", borderTop: "2px solid #e2e8f0" }}>
                      Page Total
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 800, color: "#016551", borderTop: "2px solid #e2e8f0", whiteSpace: "nowrap" }}>
                      {formatCurrency(totalSpend)}
                    </TableCell>
                    <TableCell sx={{ borderTop: "2px solid #e2e8f0" }} />
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && totalCount > 0 && (
          <PaginationControls totalCount={totalCount} page={page} setPage={setPage} offset={PAGE_SIZE} />
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={!!editRow} onClose={() => setEditRow(null)} PaperProps={{ sx: { borderRadius: "14px", width: 480 } }}>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a", borderBottom: "1px solid #f1f5f9", pb: 1.5 }}>
          Edit Marketing Spend
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 1 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" type="date" label="Date"
                value={editForm.date}
                onChange={(e) => setEditForm((p) => ({ ...p, date: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={fieldSx}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" type="number" label="Amount (₹)"
                value={editForm.amount}
                onChange={(e) => setEditForm((p) => ({ ...p, amount: e.target.value }))}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Campaign Name"
                value={editForm.campaign_name}
                onChange={(e) => setEditForm((p) => ({ ...p, campaign_name: e.target.value }))}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select fullWidth size="small" label="Channel"
                value={editForm.channel}
                onChange={(e) => setEditForm((p) => ({ ...p, channel: e.target.value }))}
                sx={fieldSx}
              >
                {channels.map((ch) => (
                  <MenuItem key={ch.id} value={ch.id}>{ch.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" multiline rows={3} label="Description"
                value={editForm.description}
                onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setEditRow(null)} size="small" sx={{ borderRadius: "8px", textTransform: "none", color: "#64748b" }}>Cancel</Button>
          <Button
            variant="contained" size="small" onClick={handleEditSave}
            disabled={!isEditValid || editLoading}
            startIcon={editLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={btnSx}
          >
            {editLoading ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default MarketingSpendTable;
